// src/reports/pdf/CaseReportPDF.jsx
// Informe PDF Gerencial del Caso
// @react-pdf/renderer

import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo2 from "../../assets/images/backgrounds/watermark-bg.png";
import { formatDateTime } from "../../utils/formatters";

// ============================
// Helpers
// ============================
const L = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const safeArr = (arr) => (Array.isArray(arr) ? arr : []);

const getInitials = (fullName) => {
  if (!fullName) return "";
  const parts = String(fullName).trim().split(/\s+/);
  if (parts.length === 1) return (parts[0].charAt(0) || "").toUpperCase();
  return (
    (parts[0].charAt(0) || "").toUpperCase() +
    (parts[parts.length - 1].charAt(0) || "").toUpperCase()
  );
};

// Siempre en MB (gerencial)
const fmtFileSizeMB = (bytes) => {
  if (bytes === null || bytes === undefined) return "—";
  const b = Number(bytes);
  if (Number.isNaN(b)) return "—";
  const mb = b / (1024 * 1024);
  if (mb <= 0) return "0.00 MB";
  if (mb < 0.01) return "< 0.01 MB";
  return `${mb.toFixed(2)} MB`;
};

const fmtDayKey = (iso) => {
  if (!iso) return "—";
  // YYYY-MM-DD (estable)
  return String(iso).slice(0, 10);
};

const fmtDayLabel = (dayKey) => {
  if (!dayKey) return "—";
  const [y, m, d] = String(dayKey).slice(0, 10).split("-").map(Number);
  const safe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)); // 12:00 UTC evita caer en día anterior

  const weekday = safe.toLocaleDateString("es-CO", {
    weekday: "long",
    timeZone: "America/Bogota",
  });
  const date = safe.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "America/Bogota",
  });

  return `${weekday}, ${date}`;
};

const groupTimelineByDay = (items) => {
  const arr = safeArr(items);
  const map = new Map();
  for (const it of arr) {
    const key = fmtDayKey(it?.eventDate);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  // mantener orden cronológico ascendente por día
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
};

// ============================
// Estilos
// ============================
const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingLeft: 28,
    paddingRight: 28,
    // Muy importante: reservar espacio para el footer fijo
    paddingBottom: 64,
    fontSize: 11,
    color: "#111827",
  },

  // Marca de agua
  watermarkWrap: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 0,
  },
  watermarkImg: {
    width: 420,
    opacity: 0.35,
    transform: "rotate(-18deg)",
  },

  content: { position: "relative", zIndex: 1 },

  // App bar
  appBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    backgroundColor: "#f3f4f6",
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  appBarLeft: { flex: 1 },
  appBarRight: { alignItems: "flex-end" },
  appBarTitle: { fontSize: 11, fontWeight: 700, color: "#111827" },
  appBarSubtitle: { fontSize: 9, color: "#4b5563", marginTop: 2 },
  appBarMeta: { fontSize: 8.5, color: "#6b7280" },

  // Header principal
  header: {
    borderBottomWidth: 1,
    borderColor: "#e5e7eb",
    paddingBottom: 10,
    marginBottom: 10,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerRight: { alignItems: "flex-end" },

  // Títulos
  h1: { fontSize: 18, marginBottom: 4, fontWeight: 700 },
  h2: {
    fontSize: 14,
    marginTop: 14,
    marginBottom: 6,
    fontWeight: 700,
    color: "#0b5ed7",
  },
  h3: {
    fontSize: 12,
    marginTop: 10,
    marginBottom: 4,
    fontWeight: 700,
    color: "#111827",
  },

  // Layout
  row: { flexDirection: "row", justifyContent: "space-between" },
  rowCenter: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  cell: { width: "49%", marginBottom: 6 },
  label: { fontSize: 9, color: "#6b7280" },
  value: { marginTop: 2, fontSize: 10 },

  // Chips
  pill: {
    fontSize: 9,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 9999,
    marginRight: 6,
    marginBottom: 4,
  },
  pillRow: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
  pillPrimary: { backgroundColor: "#eff6ff", color: "#1d4ed8" },
  pillSuccess: { backgroundColor: "#ecfdf3", color: "#15803d" },
  pillWarning: { backgroundColor: "#fef9c3", color: "#92400e" },
  pillDanger: { backgroundColor: "#fef2f2", color: "#b91c1c" },
  pillInfo: { backgroundColor: "#e0f2fe", color: "#0369a1" },
  pillMuted: { backgroundColor: "#f3f4f6", color: "#4b5563" },

  // Footer fijo
  footer: {
    position: "absolute",
    left: 28,
    right: 28,
    bottom: 18,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    justifyContent: "space-between",
  },

  // Summary cards
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
  },
  summaryCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 10,
    padding: 10,
    marginRight: 8,
    backgroundColor: "#f9fafb",
  },
  summaryCardBlue: { backgroundColor: "#eff6ff" },
  summaryCardPurple: { backgroundColor: "#f5f3ff" },
  summaryCardGreen: { backgroundColor: "#ecfdf3" },
  summaryCardLast: { marginRight: 0 },
  summaryLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
  },
  summaryDot: { width: 6, height: 6, borderRadius: 9999, marginRight: 6 },
  summaryDotBlue: { backgroundColor: "#2563eb" },
  summaryDotPurple: { backgroundColor: "#7c3aed" },
  summaryDotGreen: { backgroundColor: "#16a34a" },
  summaryLabel: { fontSize: 9, color: "#6b7280" },
  summaryValue: { fontSize: 12, fontWeight: 700, color: "#111827" },
  summaryHint: { fontSize: 8.5, color: "#6b7280", marginTop: 2 },

  // Bloques largos
  longTextBox: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 10,
    padding: 10,
    backgroundColor: "#f9fafb",
    marginTop: 4,
    fontSize: 9.5,
    lineHeight: 1.35,
    textAlign: "justify",
  },

  // Estado vencimientos
  deadlineNeutral: { backgroundColor: "#f3f4f6", borderColor: "#e5e7eb" },
  deadlineOnTrack: { backgroundColor: "#ecfdf3", borderColor: "#bbf7d0" },
  deadlineAlert: { backgroundColor: "#fef9c3", borderColor: "#facc15" },
  deadlineDanger: { backgroundColor: "#fef2f2", borderColor: "#fecaca" },

  // Indicadores
  dot: { width: 6, height: 6, borderRadius: 9999, marginRight: 6 },
  dotGreen: { backgroundColor: "#22c55e" },
  dotRed: { backgroundColor: "#ef4444" },

  // Avatar
  avatarRow: { flexDirection: "row", alignItems: "center", marginBottom: 2 },
  avatarCircle: {
    width: 18,
    height: 18,
    borderRadius: 9999,
    backgroundColor: "#e5e7eb",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  avatarInitial: { fontSize: 9, fontWeight: 700, color: "#374151" },

  // Secciones (control paginación)
  section: {
    marginTop: 14,
  },

  // ===== NOTAS (cards) =====
  noteCard: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    marginTop: 8,
    overflow: "hidden",
  },
  noteCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: "#f9fafb",
    borderBottomWidth: 1,
    borderBottomColor: "#eef2f7",
  },
  noteAccent: {
    width: 4,
    backgroundColor: "#7c3aed",
  },
  noteHeaderLeft: { flex: 1, paddingRight: 8 },
  noteTitle: { fontSize: 10.5, fontWeight: 700, color: "#111827" },
  noteMeta: { fontSize: 8.5, color: "#6b7280", marginTop: 2 },
  noteHeaderRight: { alignItems: "flex-end" },
  noteBadge: {
    fontSize: 8.5,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 9999,
    backgroundColor: "#f3f4f6",
    color: "#4b5563",
  },
  noteBadgeInternal: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
  },
  noteBodyWrap: { padding: 10 },
  noteBody: { fontSize: 9.5, lineHeight: 1.35, color: "#111827" },
  noteAttachmentsWrap: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#eef2f7",
    paddingTop: 8,
  },
  noteAttachmentsTitle: {
    fontSize: 9,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 4,
  },
  noteAttachmentLine: { fontSize: 9, color: "#374151", marginTop: 2 },

  // ===== ADJUNTOS (cards grid) =====
  attachGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 6,
  },
  attachCard: {
    width: "49%",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    marginBottom: 10,
    overflow: "hidden",
  },
  attachCardLeft: { width: 4, backgroundColor: "#2563eb" },
  attachCardInner: { padding: 10, flex: 1 },
  attachTypeRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  attachType: {
    fontSize: 8.5,
    color: "#1d4ed8",
    backgroundColor: "#eff6ff",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 9999,
  },
  attachSize: { fontSize: 8.5, color: "#6b7280" },
  attachFileName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#111827",
    marginTop: 6,
  },
  attachContentType: { fontSize: 8.5, color: "#6b7280", marginTop: 2 },
  attachMetaRow: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#eef2f7",
    paddingTop: 8,
  },
  attachMetaLine: { fontSize: 9, color: "#374151", marginTop: 2 },
  attachMetaLabel: { color: "#6b7280" },

  // ===== TIMELINE (línea + cards) =====
  timelineWrap: { marginTop: 6 },
  timelineItem: { flexDirection: "row", marginBottom: 10 },
  timelineRailCol: { width: 16, alignItems: "center" },
  timelineDot: { width: 8, height: 8, borderRadius: 9999, marginTop: 4 },
  timelineLine: {
    flexGrow: 1,
    width: 2,
    backgroundColor: "#e5e7eb",
    marginTop: 2,
  },
  timelineCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    padding: 10,
  },
  timelineTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  timelineTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#111827",
    paddingRight: 8,
    flex: 1,
  },
  timelineTag: {
    fontSize: 8.5,
    color: "#4b5563",
    backgroundColor: "#f3f4f6",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 9999,
  },
  timelineMeta: { fontSize: 8.5, color: "#6b7280", marginTop: 4 },
  timelineDesc: {
    fontSize: 9.5,
    color: "#111827",
    marginTop: 6,
    lineHeight: 1.35,
  },

  // Confidencialidad
  confidentiality: {
    fontSize: 8,
    color: "#6b7280",
    marginTop: 8,
    textAlign: "justify",
  },
  timelineDayHeader: {
    marginTop: 8,
    marginBottom: 6,
    flexDirection: "row",
    alignItems: "center",
  },
  timelineDayPill: {
    fontSize: 9,
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 9999,
    backgroundColor: "#f3f4f6",
    color: "#374151",
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  timelineDayCount: {
    fontSize: 8.5,
    color: "#6b7280",
    marginLeft: 8,
  },
  timelineGroupWrap: {
    marginLeft: 2,
    marginBottom: 4,
  },
});

// ============================
// Variantes visuales (estado/prioridad/timeline)
// ============================
const mapStatusVariant = (code, isExpired) => {
  const c = (code || "").toUpperCase();
  if (isExpired || c === "VENCIDO") return "pillDanger";
  if (c === "CERRADO") return "pillMuted";
  if (c.startsWith("FINALIZADO")) return "pillSuccess";
  if (c === "EN_TRAMITE" || c === "EN_TRAMITE_INTERNO") return "pillPrimary";
  if (c === "EN_REVISION" || c === "SOL_INFO") return "pillInfo";
  if (c === "CANCELADO" || c === "ESCALADO") return "pillDanger";
  return "pillMuted";
};

const mapPriorityVariant = (code) => {
  const c = (code || "").toUpperCase();
  if (c === "URGENTE") return "pillDanger";
  if (c === "ALTA") return "pillWarning";
  if (c === "MEDIA") return "pillPrimary";
  if (c === "BAJA") return "pillSuccess";
  return "pillMuted";
};

const buildDeadlineLabel = (data) => {
  if (!data?.isDeadlineBasedCase) return "Este caso no maneja fecha límite.";
  const base = `Fecha límite: ${formatDateTime(data?.responseDeadline)}`;
  if (data?.isExpired) return `${base} · Estado: vencido`;
  if (data?.isAlertTriggered) return `${base} · En ventana de alerta`;
  return `${base} · Al día`;
};

const mapDeadlineStyleVariant = (data) => {
  if (!data?.isDeadlineBasedCase) return "deadlineNeutral";
  if (data?.isExpired) return "deadlineDanger";
  if (data?.isAlertTriggered) return "deadlineAlert";
  return "deadlineOnTrack";
};

const mapTimelineDotColor = (eventType) => {
  const t = (eventType || "").toUpperCase();
  if (t === "CASE_CREATED") return "#22c55e";
  if (t === "STATUS_CHANGED") return "#2563eb";
  if (t === "CASE_NOTE_ADDED") return "#7c3aed";
  if (t === "CASE_UPDATED") return "#6b7280";
  return "#0f766e";
};

// ============================
// Subcomponentes
// ============================
function NoteCard({ note }) {
  const atts = safeArr(note?.attachments);
  const isInternal = !!note?.isInternal;

  return (
    // wrap={false} para que la card no se parta (look gerencial).
    // Si alguna nota fuera extremadamente larga, la empuja completa a la página siguiente.
    <View style={styles.noteCard} wrap={false}>
      <View style={{ flexDirection: "row" }}>
        <View style={styles.noteAccent} />
        <View style={{ flex: 1 }}>
          <View style={styles.noteCardHeader}>
            <View style={styles.noteHeaderLeft}>
              <Text style={styles.noteTitle}>{L(note?.title)}</Text>
              <Text style={styles.noteMeta}>
                {L(note?.authorDisplayName)} · {formatDateTime(note?.createdAt)}
              </Text>
            </View>

            <View style={styles.noteHeaderRight}>
              <Text
                style={[
                  styles.noteBadge,
                  isInternal && styles.noteBadgeInternal,
                ]}
              >
                {isInternal ? "Nota interna" : "Gestión"}
              </Text>
            </View>
          </View>

          <View style={styles.noteBodyWrap}>
            <Text style={styles.noteBody}>{L(note?.body)}</Text>

            <View style={styles.noteAttachmentsWrap}>
              <Text style={styles.noteAttachmentsTitle}>
                Adjuntos ({atts.length})
              </Text>
              {atts.length === 0 ? (
                <Text style={styles.noteAttachmentLine}>Sin adjuntos</Text>
              ) : (
                atts.map((a, idx) => (
                  <Text key={a?.id || idx} style={styles.noteAttachmentLine}>
                    • {L(a?.attachmentTypeName)}: {L(a?.fileName)} ·{" "}
                    {L(a?.contentType)} · {fmtFileSizeMB(a?.fileSize)}
                  </Text>
                ))
              )}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

function AttachmentCard({ a, isLastInRow }) {
  return (
    <View
      style={[styles.attachCard, { marginRight: isLastInRow ? 0 : 8 }]}
      wrap={false}
    >
      <View style={{ flexDirection: "row" }}>
        <View style={styles.attachCardLeft} />
        <View style={styles.attachCardInner}>
          <View style={styles.attachTypeRow}>
            <Text style={styles.attachType}>{L(a?.attachmentTypeName)}</Text>
            <Text style={styles.attachSize}>{fmtFileSizeMB(a?.fileSize)}</Text>
          </View>

          <Text style={styles.attachFileName}>{L(a?.fileName)}</Text>
          <Text style={styles.attachContentType}>{L(a?.contentType)}</Text>

          <View style={styles.attachMetaRow}>
            <Text style={styles.attachMetaLine}>
              <Text style={styles.attachMetaLabel}>Cargado por: </Text>
              {L(a?.uploadedByName)}
            </Text>
            <Text style={styles.attachMetaLine}>
              <Text style={styles.attachMetaLabel}>Fecha: </Text>
              {formatDateTime(a?.uploadedAt)}
            </Text>
            <Text style={styles.attachMetaLine}>
              <Text style={styles.attachMetaLabel}>Nota origen: </Text>
              {L(a?.sourceNoteTitle)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

function TimelineItem({ t, isLast }) {
  const dotColor = mapTimelineDotColor(t?.eventType);

  return (
    <View style={styles.timelineItem} wrap={false}>
      <View style={styles.timelineRailCol}>
        <View style={[styles.timelineDot, { backgroundColor: dotColor }]} />
        {!isLast && <View style={styles.timelineLine} />}
      </View>

      <View style={styles.timelineCard}>
        <View style={styles.timelineTitleRow}>
          <Text style={styles.timelineTitle}>{L(t?.title)}</Text>
        </View>

        <Text style={styles.timelineMeta}>
          {formatDateTime(t?.eventDate)}
          {t?.performedByUserName ? ` · Por ${t.performedByUserName}` : ""}
        </Text>

        {t?.fromStatus || t?.toStatus ? (
          <Text style={styles.timelineMeta}>
            Estado:{" "}
            {t?.fromStatus
              ? `${t.fromStatus} → ${L(t?.toStatus)}`
              : L(t?.toStatus)}
          </Text>
        ) : null}

        {t?.description ? (
          <Text style={styles.timelineDesc}>{L(t?.description)}</Text>
        ) : null}
      </View>
    </View>
  );
}

// ============================
// Componente principal
// ============================
export default function CaseReportPDF({ data }) {
  const today = new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
    .format(new Date())
    .replaceAll("-", "/");
  const notes = safeArr(data?.notes);
  const attachments = safeArr(data?.attachments);
  const timeline = safeArr(data?.timeline);

  const statusVariant = mapStatusVariant(data?.caseStatusCode, data?.isExpired);
  const priorityVariant = mapPriorityVariant(data?.priorityCode);
  const deadlineStyleVariant = mapDeadlineStyleVariant(data);

  // “Huérfanos”: agrupar título + primera card (si existe)
  const firstNote = notes.length ? notes[0] : null;
  const restNotes = notes.length > 1 ? notes.slice(1) : [];

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Marca de agua */}
        <View style={styles.watermarkWrap} fixed>
          <Image src={logo2} style={styles.watermarkImg} />
        </View>

        <View style={styles.content}>
          {/* APP BAR */}
          <View style={styles.appBar}>
            <View style={styles.appBarLeft}>
              <Text style={styles.appBarTitle}>
                NovianCore · Informe gerencial
              </Text>
              <Text style={styles.appBarSubtitle}>
                Código interno: {L(data?.internalCode)} · Tipo:{" "}
                {L(data?.caseTypeName)}
              </Text>
            </View>
            <View style={styles.appBarRight}>
              <Text style={styles.appBarMeta}>Generado: {today}</Text>
              <Text style={styles.appBarMeta}>TZ: America/Bogota</Text>
            </View>
          </View>

          {/* CABECERA */}
          <View style={styles.header}>
            <View style={styles.headerRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.h1}>{L(data?.title)}</Text>
                <Text style={{ fontSize: 10, color: "#4b5563", marginTop: 2 }}>
                  Código interno: {L(data?.internalCode)}
                </Text>
                <Text style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>
                  Tipo de caso: {L(data?.caseTypeName)}
                </Text>
              </View>

              <View style={styles.headerRight}>
                <View style={styles.pillRow}>
                  <Text
                    style={[
                      styles.pill,
                      styles[statusVariant],
                      { fontSize: 9.5 },
                    ]}
                  >
                    Estado: {L(data?.caseStatusName)}
                  </Text>
                  <Text
                    style={[
                      styles.pill,
                      styles[priorityVariant],
                      { fontSize: 9.5 },
                    ]}
                  >
                    Prioridad: {L(data?.priorityName)}
                  </Text>
                </View>

                <View style={{ marginTop: 6, alignItems: "flex-end" }}>
                  <View style={styles.rowCenter}>
                    <View
                      style={[
                        styles.dot,
                        data?.isActive ? styles.dotGreen : styles.dotRed,
                      ]}
                    />
                    <Text style={{ fontSize: 9.5 }}>
                      {data?.isActive ? "Caso activo" : "Caso inactivo"}
                    </Text>
                  </View>
                  <Text
                    style={{ fontSize: 8.5, color: "#6b7280", marginTop: 2 }}
                  >
                    Empresa: {L(data?.companyName)}
                  </Text>
                </View>
              </View>
            </View>

            {/* Summary */}
            <View style={styles.summaryRow}>
              <View style={[styles.summaryCard, styles.summaryCardBlue]}>
                <View style={styles.summaryLabelRow}>
                  <View style={[styles.summaryDot, styles.summaryDotBlue]} />
                  <Text style={styles.summaryLabel}>Días abierto</Text>
                </View>
                <Text style={styles.summaryValue}>{data?.daysOpen ?? "—"}</Text>
                <Text style={styles.summaryHint}>
                  Desde la creación hasta el cierre (o hasta hoy si sigue
                  abierto).
                </Text>
              </View>

              <View style={[styles.summaryCard, styles.summaryCardPurple]}>
                <View style={styles.summaryLabelRow}>
                  <View style={[styles.summaryDot, styles.summaryDotPurple]} />
                  <Text style={styles.summaryLabel}>Notas y gestión</Text>
                </View>
                <Text style={styles.summaryValue}>
                  {data?.totalNotes ?? 0} notas
                </Text>
                <Text style={styles.summaryHint}>
                  {data?.totalTimelineEvents ?? 0} eventos en el historial.
                </Text>
              </View>

              <View
                style={[
                  styles.summaryCard,
                  styles.summaryCardGreen,
                  styles.summaryCardLast,
                ]}
              >
                <View style={styles.summaryLabelRow}>
                  <View style={[styles.summaryDot, styles.summaryDotGreen]} />
                  <Text style={styles.summaryLabel}>Documentos</Text>
                </View>
                <Text style={styles.summaryValue}>
                  {data?.totalAttachments ?? 0} adjuntos
                </Text>
                <Text style={styles.summaryHint}>
                  Evidencias, correos, escritos y soportes del caso.
                </Text>
              </View>
            </View>
          </View>

          {/* 1. Identificación */}
          <Text style={styles.h2}>1. Identificación del caso</Text>
          <View style={styles.row}>
            <View style={styles.cell}>
              <Text style={styles.label}>Código interno</Text>
              <Text style={styles.value}>{L(data?.internalCode)}</Text>
            </View>
            <View style={styles.cell}>
              <Text style={styles.label}>Número de radicado externo</Text>
              <Text style={styles.value}>{L(data?.externalCaseNumber)}</Text>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.cell}>
              <Text style={styles.label}>Tipo de caso</Text>
              <Text style={styles.value}>{L(data?.caseTypeName)}</Text>
            </View>
            <View style={styles.cell}>
              <Text style={styles.label}>Jurisdicción / Juzgado</Text>
              <Text style={styles.value}>
                {L(data?.jurisdictionName)}{" "}
                {data?.courtName ? `· ${data.courtName}` : ""}
              </Text>
            </View>
          </View>

          <View style={{ marginTop: 6 }}>
            <Text style={styles.label}>Descripción ejecutiva</Text>
            <Text style={styles.longTextBox}>{L(data?.description)}</Text>
          </View>

          {/* 2. Partes */}
          <View style={styles.section}>
            <Text style={styles.h2}>2. Partes y responsables</Text>

            <View style={styles.row}>
              <View style={styles.cell}>
                <Text style={styles.h3}>Cliente principal</Text>
                <View style={styles.avatarRow}>
                  <View style={styles.avatarCircle}>
                    <Text style={styles.avatarInitial}>
                      {getInitials(data?.customer?.fullName)}
                    </Text>
                  </View>
                  <Text style={styles.value}>
                    {L(data?.customer?.fullName)}
                  </Text>
                </View>

                <Text style={[styles.label, { marginTop: 4 }]}>Correo</Text>
                <Text style={styles.value}>{L(data?.customer?.email)}</Text>

                <Text style={[styles.label, { marginTop: 4 }]}>Teléfono</Text>
                <Text style={styles.value}>
                  {L(data?.customer?.phoneNumber)}
                </Text>
              </View>

              <View style={styles.cell}>
                <Text style={styles.h3}>Abogado responsable</Text>
                <View style={styles.avatarRow}>
                  <View style={styles.avatarCircle}>
                    <Text style={styles.avatarInitial}>
                      {getInitials(data?.lawyerResponsible?.fullName)}
                    </Text>
                  </View>
                  <Text style={styles.value}>
                    {L(data?.lawyerResponsible?.fullName)}
                  </Text>
                </View>

                <Text style={[styles.label, { marginTop: 4 }]}>Correo</Text>
                <Text style={styles.value}>
                  {L(data?.lawyerResponsible?.email)}
                </Text>

                <Text style={[styles.label, { marginTop: 4 }]}>Teléfono</Text>
                <Text style={styles.value}>
                  {L(data?.lawyerResponsible?.phoneNumber)}
                </Text>
              </View>
            </View>

            {safeArr(data?.otherParticipants).length > 0 && (
              <View style={{ marginTop: 8 }}>
                <Text style={styles.label}>Otros participantes</Text>
                {safeArr(data?.otherParticipants).map((p, idx) => (
                  <Text key={idx} style={{ fontSize: 9.5, marginTop: 2 }}>
                    • {L(p?.fullName || p?.name)}{" "}
                    {p?.roleInCase ? `(${p.roleInCase})` : ""}
                  </Text>
                ))}
              </View>
            )}
          </View>

          {/* 3. Fechas */}
          <View style={styles.section}>
            <Text style={styles.h2}>3. Fechas clave y vencimientos</Text>

            <View style={styles.row}>
              <View style={styles.cell}>
                <Text style={styles.label}>Fecha de radicación / apertura</Text>
                <Text style={styles.value}>
                  {formatDateTime(data?.filingDate)}
                </Text>
              </View>
              <View style={styles.cell}>
                <Text style={styles.label}>Fecha de cierre (si aplica)</Text>
                <Text style={styles.value}>
                  {formatDateTime(data?.closingDate)}
                </Text>
              </View>
            </View>

            <View style={styles.row}>
              <View style={styles.cell}>
                <Text style={styles.label}>Caso basado en vencimiento</Text>
                <Text style={styles.value}>
                  {data?.isDeadlineBasedCase ? "Sí" : "No"}
                </Text>
              </View>
              <View style={styles.cell}>
                <Text style={styles.label}>
                  Días de alerta antes del vencimiento
                </Text>
                <Text style={styles.value}>
                  {data?.isDeadlineBasedCase
                    ? data?.alertDaysBefore ?? "—"
                    : "—"}
                </Text>
              </View>
            </View>

            <View style={{ marginTop: 6 }}>
              <Text style={styles.label}>Resumen de vencimiento</Text>
              <Text
                style={[
                  styles.longTextBox,
                  styles[deadlineStyleVariant],
                  { fontSize: 9.5 },
                ]}
              >
                {buildDeadlineLabel(data)}
              </Text>
            </View>
          </View>

          {/* 4. Notas de gestión (anti “título huérfano”) */}
          <View style={styles.section}>
            {firstNote ? (
              // Clave: agrupar título + primera card para evitar que el título quede solo al final de una página
              <View wrap={false} minPresenceAhead={240}>
                <Text style={styles.h2}>4. Notas de gestión</Text>
                <NoteCard note={firstNote} />
              </View>
            ) : (
              <Text style={styles.h2}>4. Notas de gestión</Text>
            )}

            {firstNote ? (
              restNotes.length ? (
                <View>
                  {restNotes.map((n, idx) => (
                    <NoteCard key={n?.id || idx} note={n} />
                  ))}
                </View>
              ) : null
            ) : (
              <Text style={{ fontSize: 9.5, color: "#6b7280" }}>
                No se han registrado notas de gestión para este caso.
              </Text>
            )}
          </View>

          {/* 5. Adjuntos (cards 2 columnas) */}
          <View style={styles.section}>
            <Text style={styles.h2}>5. Adjuntos del caso</Text>

            {attachments.length ? (
              <View style={styles.attachGrid}>
                {attachments.map((a, idx) => {
                  const isLastInRow = idx % 2 === 1;
                  return (
                    <AttachmentCard
                      key={a?.id || idx}
                      a={a}
                      isLastInRow={isLastInRow}
                    />
                  );
                })}
              </View>
            ) : (
              <Text style={{ fontSize: 9.5, color: "#6b7280" }}>
                No hay adjuntos asociados a este caso.
              </Text>
            )}
          </View>

          {/* =======================
    6. LÍNEA DE TIEMPO (AGRUPADA POR FECHA)
======================== */}
          <View style={styles.section}>
            <Text style={styles.h2}>6. Línea de tiempo del caso</Text>

            {timeline.length ? (
              <View style={styles.timelineWrap}>
                {groupTimelineByDay(timeline).map(
                  ([dayKey, events], groupIdx) => (
                    <View key={dayKey} style={styles.timelineGroupWrap}>
                      {/* Header de fecha */}
                      <View style={styles.timelineDayHeader} wrap={false}>
                        <Text style={styles.timelineDayPill}>
                          {fmtDayLabel(dayKey)}
                        </Text>
                        <Text style={styles.timelineDayCount}>
                          {events.length} evento{events.length > 1 ? "s" : ""}
                        </Text>
                      </View>

                      {/* Items del día */}
                      {events.map((t, idx) => (
                        <TimelineItem
                          key={t?.id || `${dayKey}-${idx}`}
                          t={t}
                          // Ojo: el último del grupo no pinta la línea,
                          // pero si hay más grupos, se ve “cortado” y eso está bien porque cambia la fecha.
                          isLast={idx === events.length - 1}
                        />
                      ))}
                    </View>
                  )
                )}
              </View>
            ) : (
              <Text style={{ fontSize: 9.5, color: "#6b7280" }}>
                Aún no hay eventos registrados en el historial de este caso.
              </Text>
            )}
          </View>

          {/* 7. Metadatos */}
          <View style={styles.section} wrap={false}>
            <Text style={styles.h2}>7. Metadatos del sistema</Text>

            <View
              style={{
                marginTop: 6,
                borderWidth: 1,
                borderColor: "#e5e7eb",
                borderRadius: 12,
                overflow: "hidden",
              }}
              wrap
            >
              <View
                style={{
                  flexDirection: "row",
                  backgroundColor: "#f3f4f6",
                  borderBottomWidth: 1,
                  borderBottomColor: "#e5e7eb",
                }}
              >
                <Text
                  style={{
                    width: "40%",
                    padding: 8,
                    fontWeight: 700,
                    fontSize: 9,
                  }}
                >
                  Campo
                </Text>
                <Text
                  style={{
                    width: "60%",
                    padding: 8,
                    fontWeight: 700,
                    fontSize: 9,
                  }}
                >
                  Valor
                </Text>
              </View>

              {[
                ["ID del caso (identificador técnico)", L(data?.id)],
                ["Empresa", `${L(data?.companyName)} (${L(data?.companyId)})`],
                ["Creado en sistema", formatDateTime(data?.createdAt)],
                ["Última actualización", formatDateTime(data?.updatedAt)],
              ].map(([k, v], i) => (
                <View
                  key={i}
                  style={{
                    flexDirection: "row",
                    borderBottomWidth: i === 3 ? 0 : 1,
                    borderBottomColor: "#f3f4f6",
                  }}
                >
                  <Text
                    style={{
                      width: "40%",
                      padding: 8,
                      fontSize: 9,
                      color: "#111827",
                    }}
                  >
                    {k}
                  </Text>
                  <Text
                    style={{
                      width: "60%",
                      padding: 8,
                      fontSize: 9,
                      color: "#111827",
                    }}
                  >
                    {v}
                  </Text>
                </View>
              ))}
            </View>

            <Text style={styles.confidentiality}>
              Este informe contiene información confidencial y amparada por el
              secreto profesional abogado–cliente. Su contenido no debe ser
              divulgado ni reproducido sin la autorización expresa de la firma
              responsable del caso.
            </Text>
          </View>
        </View>

        {/* Footer fijo */}
        <View style={styles.footer} fixed>
          <Text>NovianCore · Informe gerencial · {today}</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
